import { createPortal } from 'react-dom';

/**
 * Stateless dialog for choosing the scope of a recurring-event edit/delete
 * (this / this-and-following / all). All behavior is threaded via props.
 */
export function RecurringEventDialog({
  mode,
  onAction,
  onCancel,
}: {
  mode: 'edit' | 'delete' | null;
  onAction: (action: 'this' | 'following' | 'all') => void;
  onCancel: () => void;
}) {
  if (!mode) return null;
  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) return null;

  const isDelete = mode === 'delete';
  const title = isDelete ? 'Delete recurring event' : 'Edit recurring event';

  return createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 2147483647,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface, white)', borderRadius: '12px',
          maxWidth: '360px', width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '20px 24px 8px', fontSize: '16px', fontWeight: 600, color: 'var(--color-text)' }}>
          {title}
        </div>
        <div style={{ padding: '8px 24px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={() => onAction('this')}
            style={{
              padding: '10px 16px', background: 'none', border: '1px solid var(--color-border)',
              borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
              textAlign: 'left', color: 'var(--color-text)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover, #f3f4f6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            This event
          </button>
          <button
            onClick={() => onAction('following')}
            style={{
              padding: '10px 16px', background: 'none', border: '1px solid var(--color-border)',
              borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
              textAlign: 'left', color: 'var(--color-text)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover, #f3f4f6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            This and following events
          </button>
          <button
            onClick={() => onAction('all')}
            style={{
              padding: '10px 16px', background: 'none', border: '1px solid var(--color-border)',
              borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
              textAlign: 'left', color: isDelete ? '#ef4444' : 'var(--color-text)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = isDelete ? '#fef2f2' : 'var(--color-surface-hover, #f3f4f6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            All events
          </button>
        </div>
        <div style={{ padding: '0 24px 16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px', background: 'none', color: 'var(--color-text-subtle)',
              border: '1px solid var(--color-border)', borderRadius: '6px',
              fontSize: '13px', fontWeight: 500, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    portalRoot
  );
}
